import { useEffect, useRef } from 'react';
import { AlertTriangle, SearchX, Inbox, RefreshCw, WifiOff } from 'lucide-react';
import { useOnline } from '../lib/useOnline';

/**
 * The two states every data page needs, and the distinction between them.
 *
 * Roughly thirty pages each invented their own way of saying "nothing here", and the wording
 * drifted — "Sin resultados", "No hay estaciones", "No se encontraron comercios". Worse, most
 * of them conflated two different situations: a request that succeeded and found nothing, and a
 * request that failed. On Farmacias that conflation meant a dead API rendered an empty list,
 * which reads as "no hay farmacia de guardia" — never true, and the worst possible answer at 2am.
 *
 * Reaching for one of these components forces the distinction rather than leaving it to whoever
 * writes the page next.
 */

/**
 * The request failed. We do not know what is out there, and saying "nothing" would be a lie.
 *
 * With the device offline it says so instead of the page's own title («Sin conexión»: nothing
 * is wrong with the data or the app), and it tries again by itself when the connection returns.
 */
export function ErrorState({
  titulo = 'No hemos podido cargar esta información',
  detalle = 'Puede ser un problema de conexión. Inténtalo de nuevo en un momento.',
  onRetry,
  children,
  className = '',
}) {
  const online = useOnline();
  const wasOffline = useRef(!online);
  useEffect(() => {
    if (!online) { wasOffline.current = true; return; }
    if (wasOffline.current && onRetry) { wasOffline.current = false; onRetry(); }
  }, [online, onRetry]);
  const Icon = online ? AlertTriangle : WifiOff;
  return (
    <div className={`py-10 px-4 text-center ${className}`} role="alert" data-testid="error-state" data-offline={online ? undefined : 'true'}>
      <Icon size={26} className={`mx-auto mb-3 ${online ? 'text-amber-500' : 'text-slate-500'}`} aria-hidden="true" />
      <p className="text-sm font-bold text-torre-slate">{online ? titulo : 'Sin conexión'}</p>
      <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto leading-relaxed">
        {online ? detalle : 'Este dispositivo no tiene conexión a internet. Lo cargaremos en cuanto vuelva.'}
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-torre-teal text-white font-bold text-sm hover:bg-teal-700 transition-colors"
          data-testid="error-state-retry"
        >
          <RefreshCw size={14} /> Reintentar
        </button>
      )}
      {children}
    </div>
  );
}

/**
 * The request worked and there is genuinely nothing to show.
 *
 * `filtrado` distinguishes "nothing exists" from "nothing matches what you asked for" — the
 * second is the user's own doing and deserves a way out, not an apology.
 */
export function EmptyState({
  titulo = 'No hay nada que mostrar',
  detalle,
  filtrado = false,
  onClearFilters,
  /** Override the default glyph where the page has a more specific one (a bell, a map pin). */
  icono,
  children,
  className = '',
}) {
  const Icon = icono || (filtrado ? SearchX : Inbox);
  return (
    <div className={`py-10 px-4 text-center ${className}`} data-testid="empty-state">
      <Icon size={26} className="mx-auto text-slate-300 mb-3" aria-hidden="true" />
      <p className="text-sm font-bold text-torre-slate">{titulo}</p>
      {detalle && <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto leading-relaxed">{detalle}</p>}
      {filtrado && onClearFilters && (
        <button
          onClick={onClearFilters}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 text-torre-slate font-bold text-sm hover:bg-slate-200 transition-colors"
          data-testid="empty-state-clear"
        >
          Quitar filtros
        </button>
      )}
      {children}
    </div>
  );
}
